class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    if (this.parentElement !== document.body) {
    document.body.appendChild(this);
    return;
    }

    if (this.dataset.initialized === "true") return;
    this.dataset.initialized = "true";

    this.button = this.querySelector(".sticky-add-to-cart__button");
    this.label = this.querySelector(".sticky-add-to-cart__label");

    this.formId = this.dataset.productFormId;
    this.productHandle = (this.dataset.productHandle || "")
      .trim()
      .toLowerCase();

    this.productTitle = this.dataset.productTitle || "this product";

    this.scrollThreshold =
      Number.parseInt(this.dataset.scrollThreshold, 10) || 0;

    this.scrollOffset =
      Number.parseInt(this.dataset.scrollOffset, 10) || 0;

    this.scrollTarget = document.querySelector(
      this.dataset.scrollTarget
    );

    const scrollOnlyHandles = (
      this.dataset.scrollOnlyHandles || ""
    )
      .split(",")
      .map((handle) => handle.trim().toLowerCase())
      .filter(Boolean);

    this.isScrollOnlyProduct = scrollOnlyHandles.includes(
      this.productHandle
    );

    if (!this.button) {
      this.remove();
      return;
    }

    this.handleScroll = this.updateVisibility.bind(this);
    this.handleButtonClick = this.handleClick.bind(this);
    this.handleProductChange = this.syncButtonState.bind(this);

    this.button.addEventListener(
      "click",
      this.handleButtonClick
    );

    window.addEventListener("scroll", this.handleScroll, {
      passive: true,
    });

    /*
     * Variant changes normally happen somewhere inside ProductInfo.
     * Watching this area lets the sticky button react if the main
     * Add to Cart button becomes disabled, changes to Sold Out, or
     * is replaced by the theme's variant JavaScript.
     */
    if (this.scrollTarget) {
      this.scrollTarget.addEventListener(
        "change",
        this.handleProductChange
      );

      this.productObserver = new MutationObserver(() => {
        this.syncButtonState();
      });

      this.productObserver.observe(this.scrollTarget, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["disabled"],
      });
    }

    if (this.isScrollOnlyProduct) {
      this.button.setAttribute(
        "aria-label",
        `Choose purchase options for ${this.productTitle}`
      );
    }

    this.syncButtonState();
    this.updateVisibility();
  }

  disconnectedCallback() {
    document.body.classList.remove(
      "sticky-cart-is-visible"
    );
    
    this.button?.removeEventListener(
      "click",
      this.handleButtonClick
    );

    window.removeEventListener(
      "scroll",
      this.handleScroll
    );

    this.scrollTarget?.removeEventListener(
      "change",
      this.handleProductChange
    );

    this.productObserver?.disconnect();
  }

  getMainAddButton() {
    const productForm = document.getElementById(this.formId);

    if (!productForm) return null;

    return productForm.querySelector(
      'button[type="submit"][name="add"]'
    );
  }

  syncButtonState() {
    /*
     * Tuff Tred and any other scroll-only products should always
     * take the customer back to the purchase options.
     */
    if (this.isScrollOnlyProduct) {
      this.button.disabled = false;
      this.label.textContent = "Add To Cart";
      return;
    }

    const mainAddButton = this.getMainAddButton();

    if (!mainAddButton) {
      this.button.disabled = true;
      return;
    }

    this.button.disabled = mainAddButton.disabled;

    const visibleMainLabel =
      mainAddButton
        .querySelector(".button__label")
        ?.textContent?.trim() ||
      mainAddButton.textContent
        ?.replace(/\s+/g, " ")
        .trim();

    this.label.textContent =
      visibleMainLabel || "Add To Cart";
  }

  updateVisibility() {
    const shouldBeVisible =
      window.scrollY >= this.scrollThreshold;

    this.classList.toggle(
      "is-visible",
      shouldBeVisible
    );

    document.body.classList.toggle(
      "sticky-cart-is-visible",
      shouldBeVisible
    );

    this.setAttribute(
      "aria-hidden",
      shouldBeVisible ? "false" : "true"
    );
  }

  handleClick() {
    if (this.isScrollOnlyProduct) {
      this.scrollToPurchaseOptions();
      return;
    }

    /*
     * Re-query immediately before clicking in case the theme replaced
     * the product form or button during a variant change.
     */
    const mainAddButton = this.getMainAddButton();

    if (!mainAddButton || mainAddButton.disabled) return;

    /*
     * This uses the theme's existing product-form.js behavior.
     * It therefore submits the selected variant and selected quantity,
     * then uses the existing cart drawer behavior.
     */
    mainAddButton.click();

    window.setTimeout(() => {
      this.syncButtonState();
    }, 50);
  }

  scrollToPurchaseOptions() {
    const target =
      this.scrollTarget ||
      document.getElementById(this.formId);

    if (!target) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }

    const targetPosition =
      target.getBoundingClientRect().top +
      window.scrollY -
      this.scrollOffset;

    window.scrollTo({
      top: Math.max(0, targetPosition),
      behavior: "smooth",
    });

    /*
     * After scrolling, place keyboard focus inside the purchase area.
     * This does not affect normal mouse or touch users.
     */
    window.setTimeout(() => {
      const focusTarget = target.querySelector(
        [
          'variant-radios input:not([disabled])',
          'variant-selects input:not([disabled])',
          ".quantity__input:not([disabled])",
          'button[name="add"]:not([disabled])',
        ].join(",")
      );

      focusTarget?.focus({
        preventScroll: true,
      });
    }, 500);
  }
}

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define(
    "sticky-add-to-cart",
    StickyAddToCart
  );
}